import { Theme, ThemeOptions } from '@material-ui/core';
import { Overrides } from '@material-ui/core/styles/overrides';

import { colors, colours } from '@shared/theme/colours';
import { fontFamily } from '@shared/theme/units/typography';

import { initializeAccordionOverrides } from './accordion';
import { initializeAccordionDetailsOverrides } from './accordion-details';
import { initializeAccordionSummaryOverrides } from './accordion-summary';
import { initializeBackdropOverrides } from './backdrop';
import { initializeButtonOverrides } from './button';
import { initializeCheckboxOverrides } from './checkbox';
import { initializeChipOverrides } from './chip';
import { initializeDialogOverrides } from './dialog';
import { initializeDrawerOverrides } from './drawer';
import { initializeFormControlLabelOverrides } from './form-control-label';
import { initializeIconButtonOverrides } from './icon-button';
import { initializeInputOverrides } from './input';
import { initializeInputBaseOverrides } from './input-base';
import { initializeLinkOverrides } from './link';
import { initializeListOverrides } from './list';
import { initializeListItemOverrides } from './list-item';
import { initializeMenuOverrides } from './menu';
import { initializeOutlinedInputOverrides } from './outlined-input';
import { initializePaperOverrides } from './paper';
import { initializeRadioOverrides } from './radio';
import { initializeTabOverrides } from './tab';
import { initializeTableCellOverrides } from './table-cell';
import { initializeTabsOverrides } from './tabs';
import { initializeTooltipOverrides } from './tooltip';

export const initializeOverrides = (overrides: ThemeOptions['overrides'], { spacing }: Theme): Overrides => {
  return {
    MuiCssBaseline: {
      '@global': {
        body: {
          backgroundColor: '#FFFFFF',
        },
        '#root': {
          height: '100%',
        },
        a: {
          color: colours.brandNavy[900],
          cursor: 'pointer',
          textDecoration: 'none',
          touchAction: 'manipulation',

          '&:hover': {
            color: colours.brandNavy[800],
            textDecoration: 'none',
          },

          '&:active': {
            color: colours.brandNavy[700],
            textDecoration: 'none',
          },
        },
        input: {
          '&::-ms-clear, &::-ms-reveal': {
            display: 'none',
          },

          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
          },
        },
        span: {
          fontWeight: 400,
          fontFamily: fontFamily,
        },
        h3: {
          fontSize: 16,
          color: colors.black.darker,
        },
        '.MuiFormHelperText-root': {
          display: 'none',
        },
        '.icon': {
          blockSize: '24px',

          '&::before': {
            fontFamily: 'ae-marketplace !important',
            fontStyle: 'normal !important',
            fontWeight: 'normal !important',
            fontVariant: 'normal !important',
            textTransform: 'none !important',
            speak: 'none',
            lineHeight: 1,
            '-webkit-font-smoothing': 'antialiased',
            '-moz-osx-font-smoothing': 'grayscale',
          },
        },
        '.iconlg': {
          blockSize: '63px',

          '&::before': {
            fontFamily: 'as-large-icons !important',
            fontStyle: 'normal !important',
            fontWeight: 'normal !important',
            fontVariant: 'normal !important',
            textTransform: 'none !important',
            speak: 'none',
            lineHeight: 1,
            '-webkit-font-smoothing': 'antialiased',
            '-moz-osx-font-smoothing': 'grayscale',
          },
        },
      },
    },
    MuiAccordion: initializeAccordionOverrides(overrides?.MuiAccordion),
    MuiAccordionSummary: initializeAccordionSummaryOverrides(overrides?.MuiAccordionSummary),
    MuiAccordionDetails: initializeAccordionDetailsOverrides(overrides?.MuiAccordionDetails),
    MuiChip: initializeChipOverrides(overrides?.MuiChip),
    MuiTab: initializeTabOverrides(overrides?.MuiTab),
    MuiTabs: initializeTabsOverrides(overrides?.MuiTabs),
    MuiButton: initializeButtonOverrides(overrides?.MuiButton),
    MuiDrawer: initializeDrawerOverrides(overrides?.MuiDrawer),
    MuiDivider: overrides?.MuiDivider ?? {
      root: {
        backgroundColor: colours.neutral[200],
        margin: spacing(7, 0),
      },
    },
    MuiMenu: initializeMenuOverrides(overrides?.MuiMenu),
    MuiLink: initializeLinkOverrides(overrides?.MuiLink),
    MuiOutlinedInput: initializeOutlinedInputOverrides(overrides?.MuiOutlinedInput),
    MuiInputBase: initializeInputBaseOverrides(overrides?.MuiInputBase),
    MuiTooltip: initializeTooltipOverrides(overrides?.MuiTooltip),
    MuiList: initializeListOverrides(overrides?.MuiList),
    MuiListItem: initializeListItemOverrides(overrides?.MuiListItem),
    MuiPaper: initializePaperOverrides(overrides?.MuiPaper),
    MuiTableCell: initializeTableCellOverrides(overrides?.MuiTableCell),
    MuiDialog: initializeDialogOverrides(overrides?.MuiDialog),
    MuiRadio: initializeRadioOverrides(overrides?.MuiRadio),
    MuiCheckbox: initializeCheckboxOverrides(overrides?.MuiCheckbox),
    MuiInput: initializeInputOverrides(overrides?.MuiInput),
    MuiBackdrop: initializeBackdropOverrides(overrides?.MuiBackdrop),
    MuiFormControlLabel: initializeFormControlLabelOverrides(overrides?.MuiFormControlLabel),
    MuiIconButton: initializeIconButtonOverrides(overrides?.MuiIconButton),
    ...overrides,
  };
};
